/**
 * Main App Component
 * Handles authentication and routing
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from './contexts';
import { useTranslation } from './hooks/useTranslation';
import { HomeScreen } from './screens/HomeScreen';
import { TradeScreen } from './screens/TradeScreen';
import { ModernAssetsScreen } from './screens/ModernAssetsScreen';
import { ModernStatsScreen } from './screens/ModernStatsScreen';
import { ModernSettingsScreen } from './screens/ModernSettingsScreen';
import { ReferralScreen } from './screens/ReferralScreen';
import { DepositScreen } from './screens/DepositScreen';
import { AdminScreen } from './screens/AdminScreen';
import { TasksScreen } from './screens/TasksScreen';
import { BatteryScreen } from './screens/BatteryScreen';
import { StakingScreen } from './screens/StakingScreen';
import { AiTraderScreen } from './screens/AiTraderScreen';
import { TabBar } from './components/TabBar';

function App() {
  const { isAuthenticated, isLoading, error } = useAuthContext();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'home' | 'trade' | 'battery' | 'staking' | 'ai-trader' | 'assets' | 'stats' | 'referral' | 'settings' | 'deposit' | 'admin' | 'tasks'>('home');
  const [navHistory, setNavHistory] = useState<string[]>([]);

  const goBack = useCallback(() => {
    setNavHistory(h => {
      if (h.length > 0) {
        const prev = h[h.length - 1];
        setActiveTab(prev as any);
        return h.slice(0, -1);
      }
      return h;
    });
  }, []);

  // Telegram BackButton integration
  useEffect(() => {
    try {
      const tg = (window as any).Telegram?.WebApp;
      if (!tg?.BackButton) return;
      if (activeTab !== 'home' && navHistory.length > 0) {
        tg.BackButton.show();
      } else {
        tg.BackButton.hide();
      }
    } catch (_) { /* ignore */ }
  }, [activeTab, navHistory.length]);

  useEffect(() => {
    try {
      const tg = (window as any).Telegram?.WebApp;
      if (!tg?.BackButton) return;
      tg.BackButton.onClick(goBack);
      return () => { try { tg.BackButton.offClick(goBack); } catch (_) { /* */ } };
    } catch (_) { /* ignore */ }
  }, [goBack]);

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#1a1a1a',
        color: '#ffffff'
      }}>
        <div>{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#1a1a1a',
        color: '#ff4444',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2>{t('auth.authentication_error')}</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#1a1a1a',
        color: '#ffffff'
      }}>
        <div>{t('auth.not_authenticated')}</div>
      </div>
    );
  }

  // Authenticated - show app with navigation
  const navigateTo = (tab: 'home' | 'trade' | 'battery' | 'staking' | 'ai-trader' | 'assets' | 'stats' | 'referral' | 'settings' | 'deposit' | 'admin' | 'tasks') => {
    if (tab !== activeTab) {
      setNavHistory(prev => [...prev, activeTab]);
      setActiveTab(tab);
    }
  };

  const handleTabChange = (tab: 'home' | 'trade' | 'battery' | 'staking' | 'ai-trader' | 'assets' | 'stats' | 'referral' | 'settings' | 'deposit' | 'admin' | 'tasks') => {
    setNavHistory([]);
    setActiveTab(tab);
  };

  const renderScreen = () => {
    switch (activeTab) {
      case 'home':
        return <HomeScreen key="home" onNavigateToTrade={() => navigateTo('trade')} onNavigateToAssets={() => navigateTo('assets')} onNavigateToDeposit={() => navigateTo('deposit')} onNavigateToTasks={() => navigateTo('tasks')} onNavigateToStaking={() => navigateTo('staking')} />;
      case 'trade':
        return <TradeScreen key="trade" />;
      case 'assets':
        return <ModernAssetsScreen key="assets" />;
      case 'stats':
        return <ModernStatsScreen key="stats" />;
      case 'referral':
        return <ReferralScreen key="referral" />;
      case 'settings':
        return <ModernSettingsScreen key="settings" onNavigateToAdmin={() => navigateTo('admin')} onNavigateToReferral={() => navigateTo('referral')} />;
      case 'deposit':
        return <DepositScreen key="deposit" onBack={goBack} />;
      case 'admin':
        return <AdminScreen key="admin" onBack={goBack} />;
      case 'battery':
        return <BatteryScreen key="battery" />;
      case 'staking':
        return <StakingScreen key="staking" />;
      case 'ai-trader':
        return <AiTraderScreen key="ai-trader" />;
      case 'tasks':
        return <TasksScreen key="tasks" />;
      default:
        return <HomeScreen key="home" onNavigateToTrade={() => navigateTo('trade')} onNavigateToAssets={() => navigateTo('assets')} onNavigateToDeposit={() => navigateTo('deposit')} onNavigateToTasks={() => navigateTo('tasks')} onNavigateToStaking={() => navigateTo('staking')} />;
    }
  };

  return (
    <div className="app" style={{ paddingBottom: (activeTab === 'deposit' || activeTab === 'admin') ? '0' : '80px' }}>
      <div style={{ paddingTop: activeTab !== 'home' ? '8px' : '0' }}>
        {renderScreen()}
      </div>
      {(activeTab !== 'deposit' && activeTab !== 'admin') && <TabBar activeTab={activeTab === 'referral' ? 'settings' : activeTab as any} onTabChange={handleTabChange} />}
    </div>
  );
}

export default App;
